import React from 'react';
import { useFetchSubmission } from '@common/features/submission/hooks/useFetchSubmission';
import SubmissionProductContent from '@common/features/product/components/ProductContent/SubmissionProductContent';
import { IDuplicateProductInfo } from '@common/features/product/components/ProductContent/hooks/useDuplicateProductError';
import { useParams } from 'react-router';
import useAppNavigation from '../../routing/useAppNavigation';
import { ReadonlyProductContent } from '@common/features/product/components/ProductContent/ReadOnlyProductContent';
import ProductContentSkeletonLoader from '@common/features/product/components/ProductContent/ProductContentSkeletonLoader';
import { getSingleProduct, isProductRetired } from '@common/features/product/components/ProductContent/helpers';
import {
  AssociatedContentReadonlySection,
  ProductInfoReadonlySection,
} from '@common/features/product/components/ProductContent/types';

interface ISubmissionProductDetails {
  readonly?: boolean;
}

export const SubmissionProduct = ({ readonly }: ISubmissionProductDetails) => {
  const { goToProductGroup, goToProduct, goToPublishedProduct } = useAppNavigation();
  const { submissionId = '', productId = '' } = useParams();

  const { submission, isFetchingSubmission, isSubmissionFetched } = useFetchSubmission(submissionId);

  const handleRedirectToProductGroup = () => {
    goToProductGroup(submissionId);
  };

  const handleDuplicateProductView = (duplicateInfo: IDuplicateProductInfo) => {
    duplicateInfo.productGroupId
      ? goToPublishedProduct(duplicateInfo.productGroupId, duplicateInfo.productId)
      : goToProduct(duplicateInfo.submissionId!, duplicateInfo.productId);
  };

  const product = getSingleProduct(productId, submission?.products!);
  const isRetired = isProductRetired(product!);

  const associatedContentReadonlySections = {
    [AssociatedContentReadonlySection.secondaryDocumentsSelection]: isRetired,
  };

  const isMissingProductNameDetails = !product?.hasPublishedIngredientStrenghts;

  const productInfoReadonlySections = {
    [ProductInfoReadonlySection.productForm]: !isMissingProductNameDetails,
    [ProductInfoReadonlySection.activeIngredients]: !isMissingProductNameDetails,
    [ProductInfoReadonlySection.extraInformation]: !isMissingProductNameDetails,
    [ProductInfoReadonlySection.regulatorApprovedName]: !isMissingProductNameDetails,
    [ProductInfoReadonlySection.genericName]: !isMissingProductNameDetails,
    [ProductInfoReadonlySection.displayName]: !isMissingProductNameDetails,
  };

  const shouldBeEditable = submission?.isEditable;

  if (!isSubmissionFetched || isFetchingSubmission) {
    return <ProductContentSkeletonLoader />;
  }

  if (!readonly && shouldBeEditable) {
    return (
      <SubmissionProductContent
        product={product}
        submission={submission}
        onCancel={handleRedirectToProductGroup}
        onResultSuccess={handleRedirectToProductGroup}
        onDuplicateProductView={handleDuplicateProductView}
        shouldDisplayAmpIds={false}
        shouldDisplayContactGroup={false}
        associatedContentReadonlySections={associatedContentReadonlySections}
        productInfoReadonlySections={productInfoReadonlySections}
        isProductRetired={isRetired}
      />
    );
  }

  return (
    <ReadonlyProductContent
      shouldDisplayAmpIds={false}
      shouldDisplayContactGroup={false}
      dataSource={submission!}
      productId={productId}
      onCancel={handleRedirectToProductGroup}
    />
  );
};
